import { checkSession } from "@features/auth";
import { getUserOrganizationId } from "@features/auth/useAuth";
import { useAsyncMutation } from "@hooks/useAsyncMutation";
import { useI18nContext } from "@i18n/i18n-react";
import { T } from "@i18n/T";
import { preloadQuery } from "@relay";
import { Button } from "@shadcn/ui/button";
import { Checkbox } from "@shadcn/ui/checkbox";
import { Input } from "@shadcn/ui/input";
import { Label } from "@shadcn/ui/label";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { Archive, Container } from "lucide-react";
import { useState } from "react";
import { usePreloadedQuery } from "react-relay";
import { graphql } from "relay-runtime";
import { toast } from "sonner";
import { formatConfigurationLabel } from "~/components/lock/spaceConfiguration";
import { AppHeader } from "~/components/layout/AppHeader";
import { PageContainer } from "~/components/layout/PageContainer";
import type { summary_spaceIdQuery } from "./__generated__/summary_spaceIdQuery.graphql";
import type { summary_spaceIdRentMutation } from "./__generated__/summary_spaceIdRentMutation.graphql";
import type { summary_spaceIdSetContentMutation } from "./__generated__/summary_spaceIdSetContentMutation.graphql";

export const query = graphql`
	query summary_spaceIdQuery($hubName: String!) @relay_test_operation {
		hubMeta(by: { name: $hubName }) @required(action: THROW) {
			id
			name
			street
			city
			postalCode
			hub @required(action: THROW) {
				id
				availableConfigurations {
					configuration
					spaces {
						id
						frame
						position
						configuration
					}
				}
			}
		}
	}
`;

// The reservation is only created here, at the summary step, because this is
// where the user actually confirms the rental.
const rentMutation = graphql`
	mutation summary_spaceIdRentMutation($input: LockReservationCreateInput!) {
		b2BLockReservationCreate(input: $input)
	}
`;

const setContentMutation = graphql`
	mutation summary_spaceIdSetContentMutation($input: ReservationNoteSetInput!) {
		reservationNoteSet(input: $input) {
			id
			note
			... on LockReservation {
				contentNote
			}
		}
	}
`;

export const Route = createFileRoute("/$hubMetaName/swap/summary/$spaceId")({
	beforeLoad: () => {
		document.title = "bacehub | Lock";
	},
	component: RouteComponent,
	loader: async (ctx) => {
		const sessionExists = await checkSession();
		if (!sessionExists) {
			throw redirect({
				to: "/auth",
				search: { redirectToPath: `/${ctx.params.hubMetaName}/swap/select${ctx.location.searchStr}` },
			});
		}
		if (!getUserOrganizationId()) {
			throw redirect({ to: "/" });
		}
		return preloadQuery<summary_spaceIdQuery>(ctx.context.relayEnvironment, query, {
			hubName: ctx.params.hubMetaName,
		});
	},
});

function RouteComponent() {
	const navigate = useNavigate();
	const i18n = useI18nContext().LL["new-ui"];
	const { spaceId } = Route.useParams();
	const { hubMeta } = usePreloadedQuery<summary_spaceIdQuery>(query, Route.useLoaderData());

	const [rent, isRenting] = useAsyncMutation<summary_spaceIdRentMutation>(rentMutation);
	const [saveContent, isSavingContent] = useAsyncMutation<summary_spaceIdSetContentMutation>(setContentMutation);
	const isStarting = isRenting || isSavingContent;

	const [content, setContent] = useState("");

	// The terms must be accepted before the compartment can be used. The error is
	// only revealed once the user has tried to submit.
	const [acceptTerms, setAcceptTerms] = useState(false);
	const [showConfirmErrors, setShowConfirmErrors] = useState(false);

	// Find the space the user picked among the hub's available configurations so
	// we can show its details without having created a reservation yet.
	const space = hubMeta.hub.availableConfigurations.flatMap((config) => config.spaces).find((s) => s.id === spaceId);

	// The space could not be found / is no longer available: send the user back
	// to the selection screen.
	if (!space) {
		navigate({ to: "/$hubMetaName/swap/select", params: { hubMetaName: hubMeta.name } });
		return null;
	}

	const address = `${hubMeta.street}, ${hubMeta.postalCode} ${hubMeta.city}`;

	// Start the rental without opening the compartment. The organization owns
	// the reservation, so its members can access it directly from "my locks".
	const handleStartRental = async () => {
		if (!acceptTerms) {
			setShowConfirmErrors(true);
			return;
		}
		try {
			// 1. Create the reservation now that the user has confirmed.
			const reservation = await rent({ variables: { input: { spaceId } } });
			const reservationId = reservation.b2BLockReservationCreate;

			// 2. Persist the optional content before rendering the new card.
			if (content.trim()) {
				await saveContent({ variables: { input: { reservationId, note: content.trim() } } });
			}

			// 3. Done — go to "my locks" where the compartment can be opened.
			navigate({ to: "/$hubMetaName/swap/my", params: { hubMetaName: hubMeta.name } });
		} catch (error) {
			console.error(error);
			toast.error(i18n.SOMETHING_WENT_WRONG());
		}
	};

	return (
		<PageContainer
			footer={
				<Button size="lg" className="w-full shadow-xl" disabled={isStarting} onClick={handleStartRental}>
					<T>{isStarting ? i18n.LOCK_RENTING() : i18n.LOCK_SUMMARY_USE_COMPARTMENT()}</T>
				</Button>
			}
		>
			<AppHeader service="LOCK_B2B" />

			<div className="flex flex-col gap-6 px-6 pt-6">
				<h1 className="text-2xl font-breit font-medium">
					<T>{i18n.LOCK_SUMMARY_TITLE()}</T>
				</h1>

				{/* Rental details */}
				<div className="flex flex-col gap-4">
					<p className="text-sm font-breit font-medium">
						<T>{i18n.LOCK_SUMMARY_RENTAL_DETAILS()}</T>
					</p>

					<div className="flex flex-col">
						<SummaryRow
							icon={<Container className="size-6" />}
							title={i18n.LOCK_B2C_SUMMARY_HUB({ name: hubMeta.name })}
							description={address}
						/>
						<SummaryRow
							icon={<Archive className="size-6" />}
							title={i18n.LOCK_SUMMARY_COMPARTMENT_NUMBER({
								label: formatConfigurationLabel(space.configuration),
								number: `${space.frame}-${space.position}`,
							})}
							description={i18n.LOCK_SUMMARY_COMPARTMENT_NUMBER_DESC()}
						/>
					</div>
				</div>

				{/* Compartment content */}
				<div className="flex flex-col gap-2">
					<Label htmlFor="compartment-content" className="text-sm font-breit font-medium">
						<T>{i18n.LOCK_SUMMARY_CONTENT_LABEL()}</T>
					</Label>
					<Input
						id="compartment-content"
						value={content}
						onChange={(e) => setContent(e.target.value)}
						placeholder={i18n.LOCK_SUMMARY_CONTENT_PLACEHOLDER()}
					/>
					<p className="text-sm text-muted-foreground">
						<T>{i18n.LOCK_SUMMARY_CONTENT_HELP()}</T>
					</p>
				</div>

				{/* Disclaimer + mandatory confirmations */}
				<div className="flex flex-col gap-2">
					<p className="text-sm font-breit font-medium">
						<T>{i18n.LOCK_SUMMARY_DISCLAIMER_TITLE()}</T>
					</p>
					<p className="text-sm text-muted-foreground">
						<T>{i18n.LOCK_SUMMARY_DISCLAIMER_DESC()}</T>
					</p>

					<div className="flex flex-col gap-4 pt-2">
						<ConfirmationCheckbox
							id="confirm-terms"
							checked={acceptTerms}
							onChange={(next) => {
								setAcceptTerms(next);
								if (next) setShowConfirmErrors(false);
							}}
							label={i18n.LOCK_SUMMARY_CONFIRM_TERMS()}
							error={showConfirmErrors && !acceptTerms ? i18n.LOCK_SUMMARY_CONFIRM_REQUIRED() : undefined}
						/>
					</div>
				</div>
			</div>
		</PageContainer>
	);
}

/**
 * A single mandatory confirmation row: a checkbox with an accessible label and
 * an optional inline error. The required state / error is conveyed as text so it
 * doesn't rely on colour alone, and the touch target spans the whole label.
 */
function ConfirmationCheckbox(props: {
	id: string;
	checked: boolean;
	onChange: (checked: boolean) => void;
	label: string;
	error?: string;
}) {
	const errorId = `${props.id}-error`;
	return (
		<div className="flex flex-col gap-1.5">
			<label htmlFor={props.id} className="flex cursor-pointer items-start gap-3">
				<Checkbox
					id={props.id}
					checked={props.checked}
					onCheckedChange={(value) => props.onChange(value === true)}
					aria-invalid={props.error ? true : undefined}
					aria-describedby={props.error ? errorId : undefined}
					className="mt-0.5 size-5 shrink-0"
				/>
				<span className="text-sm leading-snug">
					<T>{props.label}</T>
				</span>
			</label>
			{props.error ? (
				<p id={errorId} className="pl-8 text-sm text-destructive">
					<T>{props.error}</T>
				</p>
			) : null}
		</div>
	);
}

function SummaryRow(props: { icon: React.ReactNode; title: string; description?: string }) {
	return (
		<div className="flex items-start gap-4 border-t py-5 first:border-t-0">
			<div className="flex shrink-0 items-center justify-center">{props.icon}</div>
			<div className="flex min-w-0 flex-col gap-1">
				<p className="text-base font-breit font-medium leading-none break-words">{props.title}</p>
				{props.description && <p className="text-sm text-muted-foreground break-words">{props.description}</p>}
			</div>
		</div>
	);
}
